import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { CreateEditPhasePayload } from '../../../dtos/plans.dto';
import { AlertService } from '../../../services/alert/alert.service';
import { LinkedItemsService } from '../../../services/linked-items/linked-items.service';
import { PlansService } from '../../../services/plans/plans.service';
import { PusherAssignmentChangesDTO } from '../../../services/plans/plans.service.dto';
import {
  AddAssignment,
  AddPhase,
  DeleteAssignment,
  DeletePhase,
  EditPhase,
} from '../../../state/implementation-plan/implementation-plan.actions';
import { LinkedItemsPayload } from '../../../types/payloads/linked-items.payloads';
import {
  LinkedItemParentTable,
  LinkedItemSearchOption,
  LinkedItemTargetTable,
} from '../../../types/responses/linked-items.responses';
import { PhaseAPIResponse } from '../../../types/responses/plan.responses';
import { PlansStatusSelectComponent } from '../../plans/plans-status-select/plans-status-select.component';
import { ModalComponent } from '../modal/modal.component';

@Component({
  selector: 'app-create-edit-phase-modal',
  templateUrl: './create-edit-phase-modal.component.html',
  styleUrl: './create-edit-phase-modal.component.scss',
  standalone: false,
})
export class CreateEditPhaseModalComponent implements OnInit, AfterViewInit {
  @ViewChild('modal') modal: ModalComponent;

  @ViewChild('statusSelect') statusSelect: PlansStatusSelectComponent;

  @Input() planId: number;

  @Input() displayName: string;

  @Input() isEditMode = false;

  @Input() isE2L = false;

  @Input() planDistrictId: number | undefined;

  @Input() currentUser: User;

  assignees: UserLiteDTO[] = [];

  linkedItems: LinkedItemsPayload = {
    target_id: 0,
    target_table: '',
    items: [],
  };

  phaseToEdit: PhaseAPIResponse | undefined;

  form: FormGroup;

  formSubmitted = false;

  submitLoading = false;

  serverError = '';

  LinkedItemParentTable = LinkedItemParentTable;

  LinkedItemTargetTable = LinkedItemTargetTable;

  constructor(
    private formBuilder: FormBuilder,
    private plansService: PlansService,
    private linkedItemsService: LinkedItemsService,
    private alertService: AlertService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      title: ['', Validators.required],
      description: [''],
    });
  }

  ngAfterViewInit() {
    this.modal.isOpening.subscribe(() => {
      if (this.isEditMode) {
        this.setFormValuesForEdit();
      }
    });
  }

  setFormValuesForEdit() {
    if (this.phaseToEdit) {
      this.form.patchValue({
        title: this.phaseToEdit.title,
        description: this.phaseToEdit.description,
      });

      this.assignees = this.phaseToEdit.assignments.map(
        (assignment) => assignment.user
      );
    }
  }

  get f() {
    return this.form.controls;
  }

  clearForm() {
    this.form.reset({
      title: '',
      description: '',
    });
    this.formSubmitted = false;
    this.serverError = '';
    this.assignees = [];
    this.linkedItems.items = [];
  }

  setLinkedItems(linkedItems: LinkedItemSearchOption[]) {
    this.linkedItems.items = linkedItems;
  }

  linkSelectedItems(phaseId: number) {
    this.linkedItems.target_id = phaseId;
    this.linkedItems.target_table = LinkedItemTargetTable.PLAN_PHASE;
    this.linkedItemsService.linkItems(this.linkedItems).subscribe();
  }

  onSubmit() {
    this.formSubmitted = true;
    this.submitLoading = true;
    if (this.form.invalid) {
      this.submitLoading = false;
      return;
    }

    if (this.isEditMode) {
      this.editPhase();
    } else {
      this.createPhase();
    }
  }

  addChosenUser(chosenUser: UserLiteDTO | null) {
    if (chosenUser) {
      if (!this.assignees.some((user) => user.id === chosenUser.id)) {
        this.assignees.push(chosenUser);
      }
    }
  }

  deleteChosenUser(chosenUser: UserLiteDTO | null) {
    if (chosenUser) {
      this.assignees = this.assignees.filter(
        (user) => user.id !== chosenUser.id
      );
    }
  }

  assignUsers(phaseId: number, users: UserLiteDTO[]) {
    users.forEach((user: UserLiteDTO) => {
      this.plansService
        .addPhaseAssignee(phaseId, user.id)
        .subscribe((userResp) => {
          const assigneePusher: PusherAssignmentChangesDTO = {
            for: 'implementation_phase',
            for_id: phaseId,
            implementation_phase_id: phaseId,
            assignment: userResp.item,
          };
          this.store.dispatch(new AddAssignment(assigneePusher));
        });
    });
  }

  editPhase() {
    if (this.phaseToEdit) {
      const editPhasePayload: CreateEditPhasePayload = {
        title: this.form.value.title,
        description: this.form.value.description,
        status: this.statusSelect.status,
      };
      this.plansService
        .editPhase(this.phaseToEdit.id, editPhasePayload)
        .subscribe({
          error: (error) => {
            this.submitLoading = false;
            this.serverError = error.error.message;
            setTimeout(() => {
              this.serverError = '';
            }, 3000);
          },
          next: (res) => {
            if (res) {
              this.store.dispatch(new EditPhase({ phase: res.item }));
              if (this.phaseToEdit) {
                const phaseId = this.phaseToEdit.id;

                // If user was not already assigned to the phase, then assign the user
                this.assignees.forEach((assignee) => {
                  if (
                    !this.phaseToEdit?.assignments?.some(
                      (assignment) => assignment.user.id === assignee.id
                    )
                  ) {
                    this.plansService
                      .addPhaseAssignee(phaseId, assignee.id)
                      .subscribe((response) => {
                        const assigneePusher: PusherAssignmentChangesDTO = {
                          for: 'implementation_phase',
                          for_id: phaseId,
                          implementation_phase_id: phaseId,
                          assignment: response.item,
                        };
                        this.store.dispatch(new AddAssignment(assigneePusher));
                      });
                  }
                });

                // If user was already assigned to the phase, but was removed during editing, delete the assignment
                this.phaseToEdit.assignments.forEach((assignment) => {
                  if (
                    !this.assignees.some(
                      (assignee) => assignee.id === assignment.user.id
                    )
                  ) {
                    this.plansService
                      .deletePhaseAssignee(phaseId, assignment.user.id)
                      .subscribe((response) => {
                        if (response) {
                          const assigneePusher: PusherAssignmentChangesDTO = {
                            for: 'implementation_phase',
                            for_id: phaseId,
                            implementation_phase_id: phaseId,
                            assignment,
                          };
                          this.store.dispatch(
                            new DeleteAssignment(assigneePusher)
                          );
                        }
                      });
                  }
                });
                this.linkSelectedItems(phaseId);
              }

              this.alertService.showAlert(`${this.displayName} Updated`);
              this.modal.close();
              this.submitLoading = false;
            }
          },
        });
    }
  }

  deletePhase() {
    if (this.phaseToEdit) {
      const phaseId = this.phaseToEdit.id;
      this.plansService.deletePhase(phaseId).subscribe({
        error: (error) => {
          this.submitLoading = false;
          this.serverError = error.error.message;
          setTimeout(() => {
            this.serverError = '';
          }, 3000);
        },
        next: () => {
          this.store.dispatch(new DeletePhase({ phaseId }));
          this.alertService.showAlert(`${this.displayName} Deleted`);
          this.modal.close();
        },
      });
    }
  }

  createPhase() {
    const createPhasePayload: CreateEditPhasePayload = {
      title: this.f['title'].value,
      description: this.f['description'].value,
    };

    this.plansService.createPhase(this.planId, createPhasePayload).subscribe({
      error: (error) => {
        this.submitLoading = false;
        this.serverError = error.error.message;
        setTimeout(() => {
          this.serverError = '';
        }, 3000);
      },
      next: (res) => {
        if (res) {
          this.store.dispatch(new AddPhase({ phase: res.item }));
          if (this.assignees.length > 0) {
            this.assignUsers(res.item.id, this.assignees);
          }
          this.linkSelectedItems(res.item.id);
          this.alertService.showAlert(`${this.displayName} Created`);
          this.modal.close();
          this.submitLoading = false;
        }
      },
    });

    return true;
  }
}
